/**
 * CraneLever — large gear/dial controlling a crane mechanism.
 *
 * Functions like a Dial: Turn-Table Touch spins it through notches.
 * When aligned, the crane moves a redwood log out of the path.
 * Can be locked in place with Pneumatic Anchor.
 *
 * Visually: a cylinder body with notch marks and a pointer needle,
 * identical construction pattern to the Dial interactive.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
// Side-effect import: registers Scene.prototype.beginAnimation for headless
// consumers that construct bare NullEngine scenes (same rationale as
// ValveCrank; the game itself pulls this in via core/Engine.ts).
import "@babylonjs/core/Animations/animatable";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";

export interface CraneLeverConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  totalNotches?: number;
  targetNotch?: number;
}

export class CraneLever extends InteractiveObject {
  private currentNotch = 0;
  private readonly totalNotches: number;
  private readonly targetNotch: number;
  private isLocked = false;

  private leverBody: Mesh;
  private pointerMesh: Mesh;
  private notchMeshes: Mesh[] = [];

  /**
   * Q-032 dial re-orientation. The dial used to lie flat (notch ring in
   * the XZ plane) and spin about world Y — invisible from the 2.5D +Z
   * camera. Two nested TransformNodes fix it WITHOUT touching rootMesh
   * (rootMesh.position feeds proximity checks, TTT ring anchoring,
   * AuraSight registration, and the QA hint at (22, 5.3)):
   *   - `dialTilt` (rotation.x = π/2) maps the children's local XZ notch
   *     ring into the world XY plane, facing the +Z camera.
   *   - `dialAssembly` (child of the tilt) carries the spin on its LOCAL
   *     rotation.y — pre-tilt local Y == world Z, so the spin stays fully
   *     visible in the camera plane. A single tilted node spun on
   *     rotation.y would NOT work: Babylon's Euler composition applies
   *     yaw in the parent frame, swinging the disc out of the XY plane
   *     (verified against v9.7 NullEngine).
   */
  private dialTilt: TransformNode;
  private dialAssembly: TransformNode;

  constructor(scene: Scene, config: CraneLeverConfig) {
    const size = config.size ?? new Vector3(1.2, 0.25, 1.2);
    super(scene, { ...config, size });

    this.totalNotches = config.totalNotches ?? 6;
    this.targetNotch = config.targetNotch ?? 4;

    // Camera-facing dial assembly (see field docs above). Children keep
    // their original local positions; only the parents change.
    this.dialTilt = new TransformNode(`craneLever_${config.key}_tilt`, scene);
    this.dialTilt.parent = this.rootMesh;
    this.dialTilt.rotation.x = Math.PI / 2;
    this.dialAssembly = new TransformNode(
      `craneLever_${config.key}_assembly`,
      scene,
    );
    this.dialAssembly.parent = this.dialTilt;

    // Create the lever body (cylinder, rust-tinted)
    this.leverBody = MeshBuilder.CreateCylinder(
      `craneLever_${config.key}_body`,
      { diameter: size.x, height: size.y, tessellation: 32 },
      scene,
    );
    this.leverBody.parent = this.dialAssembly;
    this.leverBody.position = Vector3.Zero();

    const bodyMat = new StandardMaterial(`craneLever_${config.key}_bodyMat`, scene);
    bodyMat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
    bodyMat.specularColor = new Color3(0.2, 0.15, 0.05);
    bodyMat.emissiveColor = Color3.Black();
    this.leverBody.material = bodyMat;
    this.material = bodyMat;

    // Create notch marks around the perimeter
    const radius = size.x * 0.45;
    for (let i = 0; i < this.totalNotches; i++) {
      const angle = (i / this.totalNotches) * Math.PI * 2;
      const notch = MeshBuilder.CreateCylinder(
        `craneLever_${config.key}_notch_${i}`,
        { diameter: 0.08, height: size.y + 0.03, tessellation: 8 },
        scene,
      );
      notch.parent = this.dialAssembly;
      notch.position = new Vector3(
        Math.cos(angle) * radius,
        0,
        Math.sin(angle) * radius,
      );

      const notchMat = new StandardMaterial(
        `craneLever_${config.key}_notch_${i}_mat`,
        scene,
      );
      notchMat.diffuseColor =
        i === this.targetNotch
          ? Color3.FromHexString(COLORS.EMERALD)
          : Color3.FromHexString(COLORS.BRASS);
      notchMat.emissiveColor =
        i === this.targetNotch
          ? Color3.FromHexString(COLORS.EMERALD).scale(0.3)
          : Color3.Black();
      notchMat.specularColor = Color3.Black();
      notch.material = notchMat;
      this.notchMeshes.push(notch);
    }

    // Pointer needle
    this.pointerMesh = MeshBuilder.CreateBox(
      `craneLever_${config.key}_pointer`,
      { width: 0.06, height: size.y + 0.06, depth: radius * 0.75 },
      scene,
    );
    this.pointerMesh.parent = this.dialAssembly;
    this.pointerMesh.position = new Vector3(0, 0, radius * 0.375);

    const pointerMat = new StandardMaterial(
      `craneLever_${config.key}_pointerMat`,
      scene,
    );
    pointerMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    pointerMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.15);
    pointerMat.specularColor = Color3.Black();
    this.pointerMesh.material = pointerMat;

    // Support strut: the re-oriented dial is a vertical disc whose bottom
    // sits ~0.5u above the platform top, so it read as floating. A single
    // cheap cylinder mounts it like a wheel on a post. Parented to
    // rootMesh (must NOT spin with the assembly), tucked slightly behind
    // the disc face (-Z) so it can't z-fight, and deliberately NOT added
    // to any shadow-caster list (L1 caster trim, commit cd1202f).
    const strut = MeshBuilder.CreateCylinder(
      `craneLever_${config.key}_strut`,
      { diameter: 0.14, height: size.x + 0.1, tessellation: 10 },
      scene,
    );
    strut.parent = this.rootMesh;
    strut.position = new Vector3(0, -(size.x + 0.1) / 2, -0.2);
    strut.material = bodyMat;
    strut.isPickable = false;
    strut.receiveShadows = false;

    // Set initial rotation
    this.updatePointerRotation(false);
  }

  /**
   * Test/QA introspection: the spin node (local rotation.y carries the
   * notch angle) — see the dialTilt/dialAssembly field docs.
   */
  get dialAssemblyNode(): TransformNode {
    return this.dialAssembly;
  }

  /** Spin the lever by one notch. Returns true if now aligned. */
  spin(): boolean {
    if (this.isLocked || this.state === "solved") return false;

    this.currentNotch = (this.currentNotch + 1) % this.totalNotches;
    this.updatePointerRotation(true);

    const aligned = this.currentNotch === this.targetNotch;

    if (aligned) {
      this.state = "active";
      this.updateMaterialForState();
      gameEvents.emit(GameEvents.CRANE_LEVER_ALIGNED, this.key);
    } else {
      this.state = "idle";
      this.updateMaterialForState();
      gameEvents.emit(GameEvents.CRANE_LEVER_MISALIGNED, this.key);
    }

    return aligned;
  }

  lock(): void {
    this.isLocked = true;
    const pointerMat = this.pointerMesh.material as StandardMaterial;
    pointerMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(0.4);
    gameEvents.emit(GameEvents.CRANE_LEVER_LOCKED, this.key);
  }

  unlock(): void {
    this.isLocked = false;
    const pointerMat = this.pointerMesh.material as StandardMaterial;
    pointerMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.15);
    gameEvents.emit(GameEvents.CRANE_LEVER_UNLOCKED, this.key);
  }

  onInteract(abilityId: string): boolean {
    if (abilityId === "turnTableTouch") {
      return this.spin();
    }
    if (abilityId === "pneumaticAnchor") {
      if (this.isLocked) {
        this.unlock();
      } else {
        this.lock();
      }
      return true;
    }
    return false;
  }

  get aligned(): boolean {
    return this.currentNotch === this.targetNotch;
  }

  get locked(): boolean {
    return this.isLocked;
  }

  private updatePointerRotation(animate: boolean): void {
    const targetAngle = (this.currentNotch / this.totalNotches) * Math.PI * 2;

    if (!animate) {
      this.dialAssembly.rotation.y = targetAngle;
      return;
    }

    const fps = 30;
    const frames = 6;

    const rotAnim = new Animation(
      `craneSpin_${this.key}_${Date.now()}`,
      "rotation.y",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );

    const currentAngle = this.dialAssembly.rotation.y;
    let target = targetAngle;
    if (target <= currentAngle) {
      target += Math.PI * 2;
    }

    rotAnim.setKeys([
      { frame: 0, value: currentAngle },
      { frame: frames, value: target },
    ]);

    this.dialAssembly.animations = [rotAnim];
    this.scene.beginAnimation(this.dialAssembly, 0, frames, false, 1, () => {
      this.dialAssembly.rotation.y = targetAngle;
    });
  }
}
